// Composer Studio — 미배치 풀 위젯 hover 미니 미리보기 팝오버.
//
// 작성 탭 미배치 풀에서 위젯 행에 커서를 올리면(그 바인딩이 이미 양식 어딘가에 배치돼 있을 때) 커서 옆에
// 툴팁식 팝오버로 "그 바인딩이 놓인 보고서 부분"의 실제 렌더 미니 미리보기를 자동 표시한다. 팝아웃 창의
// highlight-node 방송(F8 팝업 하이라이트)과 병행 — 이건 에디터 내부의 자동 툴팁이라 별도.
//
// 렌더는 미리보기/팝아웃과 동일 파이프를 재사용(새 렌더 발명 없음): template.body를 [컨텍스트 서브트리]로
// 프룬한 사본 → mergePreviewContent → applySimulatedApproval → PagedEditBody. 문서(A4) 폭을 팝오버 폭에
// 맞춰 scale(폭 맞춤)한다. pointer-events:none(커서 이벤트를 뺏지 않아 깜빡임 방지) + position:fixed.

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { pagePx } from '@safetyhub/report-composer';
import type {
  ComposerNode,
  ReportTemplate,
  BindingPlacement,
  BindingProvenanceContext,
} from '@safetyhub/report-composer';
import {
  PagedEditBody,
  mergePreviewContent,
  applySimulatedApproval,
  COMPOSER_SPECIAL_COMPONENTS,
} from './previewRender';

/** 팝오버 최대 폭(문서 스케일 기준) / 문서 영역 최대 높이(px). 넘치면 대상 중심으로 크롭. */
const MAX_WIDTH = 480;
// 2026-07-16 사용자: 서브트리 박스 크롭 도입 후 창이 작아져 높이 상향(480 — 종전 고정 크롭 시절 체감 크기).
const MAX_DOC_HEIGHT = 480;
/** footer(브레드크럼+조작 가이드 2줄) + 패딩 대략치 — 세로 클램프 여유 계산용. */
const CHROME_H = 56;
/** anchor(풀 행)와 팝오버 사이 간격(px). */
const GAP = 12;
/**
 * 셀 바인딩일 때의 최소 가독 스케일 — 폭 맞춤 스케일(1152px 문서→0.42)은 표가 너무 작아
 * 셀을 지목해도 읽히지 않음(2026-07-16 사용자 제보). 이보다 작아지면 이 스케일로 올리고,
 * 넘치는 부분은 대상 셀 **중심 크롭**(가로+세로 스크롤 센터링)으로 보여준다.
 */
const CELL_MIN_SCALE = 0.75;

/** 휠 줌 배율 범위/스텝 — 실제 렌더 스케일 = 기본 스케일 × zoom. 커서 기준 확대/축소. */
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 1.1;

/** node.id 매칭 노드를 찾고 그 노드의 가장 가까운 조상 group/stack 컨테이너를 함께 반환. */
function findNodeWithGroup(
  nodes: ComposerNode[],
  targetId: string,
  ancestorGroup: ComposerNode | null,
): { node: ComposerNode; ancestorGroup: ComposerNode | null } | null {
  for (const node of nodes) {
    if (node.id === targetId) return { node, ancestorGroup };
    // group/stack은 "자식 컨테이너"라 조상 group 후보. 그 외 컨테이너(repeat/branch/table)는 조상 group 갱신 안 함.
    const nextGroup =
      node.kind === 'group' || node.kind === 'stack' ? node : ancestorGroup;
    if (node.kind === 'group' || node.kind === 'stack') {
      const r = findNodeWithGroup(node.children, targetId, nextGroup);
      if (r) return r;
    } else if (node.kind === 'repeat') {
      const r = findNodeWithGroup(node.template.children, targetId, nextGroup);
      if (r) return r;
    } else if (node.kind === 'branch') {
      for (const slot of node.branches) {
        const r = findNodeWithGroup(slot.children, targetId, nextGroup);
        if (r) return r;
      }
    } else if (node.kind === 'table') {
      // 표 셀 바인딩의 placement.nodeId는 표 노드 id라 위 id 매칭에서 이미 잡힘. 셀 content에 직접
      // 바인딩 노드가 있으면 그것도 찾되, 조상 group은 표 바깥 컨텍스트를 유지.
      for (const cell of node.cells) {
        if (cell.content) {
          const r = findNodeWithGroup(cell.content, targetId, ancestorGroup);
          if (r) return r;
        }
      }
    }
  }
  return null;
}

/**
 * placement.nodeId로 template.body에서 "컨텍스트 서브트리" 1개를 선택한다.
 *  ① 그 노드가 table이면 그 table 노드
 *  ② 아니면 가장 가까운 조상 group/stack, 없으면 그 노드 자신
 */
function pickContextSubtree(body: ComposerNode[], nodeId: string): ComposerNode | null {
  const found = findNodeWithGroup(body, nodeId, null);
  if (!found) return null;
  if (found.node.kind === 'table') return found.node;
  return found.ancestorGroup ?? found.node;
}

/**
 * 자식(A4 문서)을 scale로 축소해 렌더한다. transform:scale은 레이아웃 크기에 영향이 없어
 * 바깥 래퍼가 실측(offset*scale)만큼 공간을 확보한다(PreviewPopoutPage.ScaledDocument와 동형).
 * onMeasure: 실측 완료(스케일 반영 치수) 통지 — 부모의 중심 크롭 스크롤이 이 시점 이후에 걸린다.
 */
function ScaledDocument({
  scale,
  onMeasure,
  children,
}: {
  scale: number;
  onMeasure?: (size: { w: number; h: number }) => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const onMeasureRef = useRef(onMeasure);
  onMeasureRef.current = onMeasure;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const next = { w: el.offsetWidth, h: el.offsetHeight };
      setSize(next);
      onMeasureRef.current?.({ w: next.w * scale, h: next.h * scale });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scale]);
  return (
    <div style={{ width: size.w * scale, height: size.h * scale }}>
      <div
        ref={ref}
        style={{ width: 'fit-content', transform: `scale(${scale})`, transformOrigin: 'top left' }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * 미배치 풀 위젯 hover 미니 미리보기 팝오버.
 * @param template 현재 편집 중 템플릿(미리보기 sync 스냅샷과 동일).
 * @param content  미리보기 resolve content(실데이터면 previewResult.content, 없으면 {}).
 * @param hasRealData 실데이터 반영 여부 — bindingPlaceholders 모드 구동(미리보기 탭과 동일 정책:
 *                    실데이터면 'unresolvable-only'(미해소만 amber), 아니면 true(전체 회색)).
 * @param approvalSignedLevel 결재 서명 단계 모사 값(2026-09-04) — 캔버스·팝아웃과 동일 주입.
 * @param placement 그 바인딩의 첫 배치(nodeId/cell/breadcrumb).
 * @param anchorRect 풀 행 엘리먼트의 viewport rect — 팝오버 위치 기준.
 * @param onMouseEnter 팝오버 진입 — 부모의 숨김 유예 타이머 취소(hover 유지).
 * @param onMouseLeave 팝오버 이탈 — 부모가 즉시 숨김.
 */
export function PoolHoverPreview({
  template,
  content,
  hasRealData,
  approvalSignedLevel,
  bindingProvenance,
  placement,
  anchorRect,
  onMouseEnter,
  onMouseLeave,
}: {
  template: ReportTemplate;
  content: Record<string, unknown>;
  hasRealData: boolean;
  approvalSignedLevel?: number;
  /** 값 출처 표시 재료 — 미리보기 탭/팝아웃과 같은 밑줄을 이 미니 미리보기도 그린다. */
  bindingProvenance?: BindingProvenanceContext;
  placement: BindingPlacement;
  anchorRect: DOMRect;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  // 문서 클립 컨테이너(maxHeight+overflow hidden) — 셀 강조 시 스크롤 보정 대상.
  const clipRef = useRef<HTMLDivElement>(null);

  // 컨텍스트 서브트리로 프룬한 템플릿 사본 → 미리보기와 동일 병합/시뮬레이션 결재.
  const prepared = useMemo(() => {
    const subtree = pickContextSubtree(template.body, placement.nodeId);
    if (!subtree) return null;
    const pruned: ReportTemplate = { ...template, body: [subtree] };
    const merged = mergePreviewContent(pruned, content);
    const data = applySimulatedApproval(pruned, merged, approvalSignedLevel);
    return { pruned, data, subtree };
  }, [template, content, approvalSignedLevel, placement.nodeId]);

  // 실측 완료 신호(스케일 반영 문서 치수) — 중심 크롭 스크롤은 실측 이후에만 유효(마운트 직후엔
  // ScaledDocument 래퍼가 0×0이라 scrollTop 보정이 no-op가 되는 함정 — 2026-07-16 "대상이 상단" 제보).
  const [docSize, setDocSize] = useState<{ w: number; h: number } | null>(null);

  // ── 사용자 인터랙션(드래그 패닝 / 휠 줌) 상태 ──
  // zoom: 기본 스케일에 곱하는 배율(휠). manualRef: 사용자가 패닝/줌을 시작하면 true —
  // 자동 센터링 재실행(docSize 변화)이 사용자 스크롤을 덮지 않게 가드. placement 전환 시 리셋.
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const manualRef = useRef(false);
  const [panning, setPanning] = useState(false);
  // 드래그 시작 스냅샷(clientXY + scroll). 커서 캡처 중에만 non-null.
  const dragRef = useRef<{ x: number; y: number; sl: number; st: number } | null>(null);
  // 휠 줌 커밋(zoom state 변경) 후 커서 기준 scroll 보정을 위한 대기 스냅샷.
  const pendingZoomRef = useRef<{
    scrollLeft: number;
    scrollTop: number;
    cx: number;
    cy: number;
    ratio: number;
  } | null>(null);

  // 서브트리 콘텐츠 박스(문서 **원본 px** — 스케일 무관). 콘텐츠 크롭 박스가 이 값 × 유효 스케일로
  // 크기를 잡아, 줌 시 크롭 박스도 함께 커진다(2026-07-16 제보: 뷰포트가 서브트리 크기에 고정돼
  // 확대하면 바깥이 잘리고 창 전체를 못 씀 → 창=고정 뷰포트, 크롭=콘텐츠 쪽으로 재편).
  const [subtreeBox, setSubtreeBox] = useState<{
    left: number;
    top: number;
    w: number;
    h: number;
  } | null>(null);
  // 콘텐츠 크롭 박스/문서 원점(offset 래퍼) — 측정·센터링 기준.
  const cropRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef<HTMLDivElement>(null);

  // placement 전환 시(같은 마운트에서 다른 행으로 교체) 줌/수동/측정 상태 리셋 — 센터링 효과보다
  // 먼저 정의해 layout effect 실행 순서상 리셋이 앞서게 한다.
  useLayoutEffect(() => {
    setZoom(1);
    zoomRef.current = 1;
    manualRef.current = false;
    dragRef.current = null;
    pendingZoomRef.current = null;
    setSubtreeBox(null);
    const clip = clipRef.current;
    if (clip) {
      clip.scrollLeft = 0;
      clip.scrollTop = 0;
    }
  }, [placement]);

  // 기본 스케일(줌 제외) — 렌더 본문과 측정 효과가 공유(동일 수식 이중 유지 방지).
  const baseScale = useMemo(() => {
    const docW = pagePx(template.page).w;
    const fit = Math.min(1, MAX_WIDTH / docW);
    return placement.cell ? Math.min(1, Math.max(fit, CELL_MIN_SCALE)) : fit;
  }, [template.page, placement.cell]);

  // ── 대상 강조 + 측정 + 초기 센터링 ──
  // 1패스(subtreeBox 미측정): 문서를 숨긴 채 렌더 후 서브트리 원본 박스 측정 → setSubtreeBox.
  // 2패스(측정 완료): 크롭 박스가 서브트리 크기로 잡힌 상태에서 대상 outline + 창 중앙 스크롤.
  // 창(clip)은 480×480 고정 뷰포트 — 크롭 박스(콘텐츠)가 줌과 함께 커져 창 전체를 활용한다.
  useLayoutEffect(() => {
    if (!prepared || !docSize) return;
    const root = boxRef.current;
    if (!root) return;
    // 대상 탐색(셀 앵커 우선 → 노드 앵커).
    let target: HTMLElement | null = null;
    let outlineOffset = '2px';
    if (placement.cell) {
      target = root.querySelector<HTMLElement>(
        `[data-composer-cell="${placement.nodeId}::${placement.cell.row}::${placement.cell.col}"]`,
      );
      // 셀은 이웃과 밀착이라 outline을 안쪽으로(-2px) — 옆 셀 침범 없이 셀 자체를 지목.
      outlineOffset = '-2px';
    }
    let outlined = true;
    if (!target) {
      outlined = !(prepared.subtree.kind === 'table' && prepared.subtree.id === placement.nodeId);
      target = root.querySelector<HTMLElement>(`[data-composer-node-id="${placement.nodeId}"]`);
    }
    if (!target) return;

    // 1패스 — 서브트리 박스 측정(문서 원본 px). offsetRef는 이 시점 margin 0이라 원점=문서 원점.
    if (!subtreeBox) {
      const originEl = offsetRef.current;
      const subtreeEl =
        root.querySelector<HTMLElement>(`[data-composer-node-id="${prepared.subtree.id}"]`) ??
        target;
      if (!originEl) return;
      const eff = baseScale * zoomRef.current; // 측정 시점 유효 스케일(리셋 직후라 zoom=1)
      const originRect = originEl.getBoundingClientRect();
      const subRect = subtreeEl.getBoundingClientRect();
      const PAD = 4; // 외곽선 여유(표시 px) — 원본 좌표로 환산해 저장
      const padN = PAD / eff;
      setSubtreeBox({
        left: Math.max(0, (subRect.left - originRect.left) / eff - padN),
        top: Math.max(0, (subRect.top - originRect.top) / eff - padN),
        w: subRect.width / eff + padN * 2,
        h: subRect.height / eff + padN * 2,
      });
      return; // 다음 패스(크롭 박스 사이징 후)에서 outline·센터링
    }

    // 2패스 — outline + 초기 센터링(사용자 패닝/줌 시작 후엔 뷰포트를 덮지 않음).
    const prevOutline = target.style.outline;
    const prevOffset = target.style.outlineOffset;
    if (outlined) {
      target.style.outline = '2px solid #6366f1'; // brand-500
      target.style.outlineOffset = outlineOffset;
    }
    const clip = clipRef.current;
    const crop = cropRef.current;
    if (clip && crop && !manualRef.current) {
      const clipRect = clip.getBoundingClientRect();
      const cropRect = crop.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const PAD = 4;
      // 크롭 박스 콘텐츠 좌표계 기준 대상 위치(스크롤 반영 무관 — cropRect가 live 좌표).
      const targetLeftInCrop = targetRect.left - cropRect.left;
      const targetTopInCrop = targetRect.top - cropRect.top;
      // 가로: 대상이 창보다 넓으면(넓은 행 셀) 중심 크롭은 라벨(왼쪽)을 잘라내므로 왼쪽 정렬,
      //       좁은 대상은 중앙. 세로는 중앙. 경계는 브라우저 스크롤 클램프가 처리.
      const wantX =
        targetRect.width >= clipRect.width
          ? targetLeftInCrop - PAD
          : targetLeftInCrop + targetRect.width / 2 - clipRect.width / 2;
      const wantY = targetTopInCrop + targetRect.height / 2 - clipRect.height / 2;
      clip.scrollLeft = Math.max(0, wantX);
      clip.scrollTop = Math.max(0, wantY);
    }
    return () => {
      if (outlined && target) {
        target.style.outline = prevOutline;
        target.style.outlineOffset = prevOffset;
      }
    };
  }, [prepared, placement, docSize, subtreeBox, baseScale]);

  // ── 휠 줌(커서 기준) ── native listener(passive:false)로 페이지/패널 스크롤 유출 차단.
  // deltaY로 zoom in/out. 커밋 시점의 scroll+커서 위치를 ref에 담아 두고, 재렌더 후 layout
  // effect에서 커서 아래 콘텐츠 점이 고정되도록 scroll 보정(CanvasViewport 줌 앵커와 동형).
  useEffect(() => {
    const clip = clipRef.current;
    if (!clip) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const oldZoom = zoomRef.current;
      const factor = Math.pow(ZOOM_STEP, -e.deltaY / 100);
      const newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, oldZoom * factor));
      if (newZoom === oldZoom) return;
      const rect = clip.getBoundingClientRect();
      pendingZoomRef.current = {
        scrollLeft: clip.scrollLeft,
        scrollTop: clip.scrollTop,
        cx: e.clientX - rect.left,
        cy: e.clientY - rect.top,
        ratio: newZoom / oldZoom,
      };
      manualRef.current = true;
      zoomRef.current = newZoom;
      setZoom(newZoom);
    };
    clip.addEventListener('wheel', onWheel, { passive: false });
    return () => clip.removeEventListener('wheel', onWheel);
  }, [prepared]);

  // 줌 커밋 후 커서 기준 scroll 보정 — newScroll = (oldScroll + cursorInClip)·ratio − cursorInClip.
  // 콘텐츠 폭(= size.w × scale)은 scale prop이 즉시 반영돼 이 시점 scrollable extent가 최신.
  useLayoutEffect(() => {
    const p = pendingZoomRef.current;
    if (!p) return;
    pendingZoomRef.current = null;
    const clip = clipRef.current;
    if (!clip) return;
    clip.scrollLeft = (p.scrollLeft + p.cx) * p.ratio - p.cx;
    clip.scrollTop = (p.scrollTop + p.cy) * p.ratio - p.cy;
  }, [zoom]);

  if (!prepared) return null;

  // 실제 렌더 스케일 = 기본 스케일(useMemo baseScale — 셀 바인딩 최소 가독 0.75 보장) × 휠 줌 배율.
  const effectiveScale = baseScale * zoom;

  // 위치 — 기본은 anchor 오른쪽. 화면 밖이면 왼쪽으로, 세로는 뷰포트 안으로 클램프(anchorRect 직접 계산,
  // 측정 async 없이 결정적 — 깜빡임 방지). pointer-events:auto로 팝오버 자체가 드래그/휠을 받는다
  // (진입 시 부모 숨김 유예 취소 → hover 유지).
  const gap = GAP;
  let left = anchorRect.right + gap;
  if (left + MAX_WIDTH + 16 > window.innerWidth) {
    left = Math.max(8, anchorRect.left - MAX_WIDTH - gap);
  }
  const maxTop = window.innerHeight - (MAX_DOC_HEIGHT + CHROME_H + 16);
  const top = Math.min(Math.max(8, anchorRect.top), Math.max(8, maxTop));

  const breadcrumb = placement.breadcrumb.join(' › ');

  return (
    // eslint-disable-next-line local/no-self-painted-card -- hover 시 떠오르는 팝오버(position:fixed·zIndex 85는
    // 인라인 style이라 규칙의 "떠 있는 것" 제외 조건에 안 걸렸을 뿐이다). Card 갈래가 아니다.
    <div
      ref={boxRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'fixed',
        left,
        top,
        // 우클릭 메뉴(z-[80])·모달 저작(모달 z-[70]/서브 z-[80]) 위에서도 보여야 함(메뉴 항목 hover·
        // 모달 편집 중 미리보기, 2026-07-16). 일시적 hover 표면이라 최상층 예외 — 상시 UI 아님.
        zIndex: 85,
        pointerEvents: 'auto',
        maxWidth: MAX_WIDTH + 24,
      }}
      className="overflow-hidden rounded-card border border-gray-300 bg-white shadow-xl"
    >
      {/* 고정 크기 창(480×480, 회색 바탕) = 클립 뷰포트 — 위젯마다 크기가 달라지지 않고, 줌 시
          크롭 박스(콘텐츠)가 함께 커져 창 전체를 활용(2026-07-16 사용자 확정 — 종전엔 뷰포트가
          서브트리 크기에 고정돼 확대분이 잘림). 드래그 패닝(scroll)·휠 커서 기준 줌. */}
      <div
        ref={clipRef}
        onPointerDown={(e) => {
          const clip = clipRef.current;
          if (!clip) return;
          e.preventDefault(); // 텍스트 선택 방지
          dragRef.current = {
            x: e.clientX,
            y: e.clientY,
            sl: clip.scrollLeft,
            st: clip.scrollTop,
          };
          manualRef.current = true;
          setPanning(true);
          clip.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const d = dragRef.current;
          const clip = clipRef.current;
          if (!d || !clip) return;
          clip.scrollLeft = d.sl - (e.clientX - d.x);
          clip.scrollTop = d.st - (e.clientY - d.y);
        }}
        onPointerUp={(e) => {
          dragRef.current = null;
          setPanning(false);
          const clip = clipRef.current;
          if (clip?.hasPointerCapture(e.pointerId)) clip.releasePointerCapture(e.pointerId);
        }}
        onPointerCancel={(e) => {
          dragRef.current = null;
          setPanning(false);
          const clip = clipRef.current;
          if (clip?.hasPointerCapture(e.pointerId)) clip.releasePointerCapture(e.pointerId);
        }}
        className="flex bg-gray-100"
        style={{
          width: MAX_WIDTH,
          height: MAX_DOC_HEIGHT,
          overflow: 'hidden',
          cursor: panning ? 'grabbing' : 'grab',
          userSelect: 'none',
          touchAction: 'none',
        }}
      >
        {/* 크롭 박스 — 서브트리 원본 박스 × 유효 스케일. 창보다 작으면 margin:auto safe-center
            (회색 바탕=명백한 창 여백), 크면 창 스크롤(패닝)의 콘텐츠. 측정 전엔 숨김(1패스). */}
        <div
          ref={cropRef}
          style={{
            margin: 'auto',
            flexShrink: 0,
            overflow: 'hidden',
            background: '#fff',
            visibility: subtreeBox ? 'visible' : 'hidden',
            width: subtreeBox ? subtreeBox.w * effectiveScale : undefined,
            height: subtreeBox ? subtreeBox.h * effectiveScale : undefined,
          }}
        >
          {/* offset 래퍼 — 문서를 서브트리 원점만큼 끌어올려 크롭 박스에 서브트리만 보이게.
              측정 전(margin 0)엔 원점=문서 원점이라 1패스 측정 기준. */}
          <div
            ref={offsetRef}
            style={{
              marginLeft: subtreeBox ? -subtreeBox.left * effectiveScale : 0,
              marginTop: subtreeBox ? -subtreeBox.top * effectiveScale : 0,
            }}
          >
            <ScaledDocument scale={effectiveScale} onMeasure={setDocSize}>
              <PagedEditBody
                template={prepared.pruned}
                data={prepared.data}
                specialComponents={COMPOSER_SPECIAL_COMPONENTS}
                bindingPlaceholders={hasRealData ? 'unresolvable-only' : true}
                bindingProvenance={bindingProvenance}
              />
            </ScaledDocument>
          </div>
        </div>
      </div>
      {/* footer — 1줄: 위치 브레드크럼 / 2줄: 조작 가이드(커서를 창으로 옮겨야 동작함을 명시,
          2026-07-16 사용자 요청 — hover 툴팁이라 상호작용 가능 여부가 자명하지 않음). */}
      <div className="border-t border-gray-200 bg-gray-50 px-2 py-1">
        {breadcrumb ? (
          <div className="truncate text-[11px] text-gray-500">{breadcrumb}</div>
        ) : null}
        <div className="truncate text-[10px] text-gray-400">
          마우스를 이 창 안으로 옮기면 — 드래그: 이동 · 휠: 확대/축소
        </div>
      </div>
    </div>
  );
}
